import React, { ReactElement, useEffect, useState } from "react";

import { Box } from "@chakra-ui/react";
import styled from "@emotion/styled";
import { Viewer, Worker } from "@react-pdf-viewer/core";
import "@react-pdf-viewer/core/lib/styles/index.css";
import {
  ToolbarProps,
  ToolbarSlot,
  defaultLayoutPlugin,
} from "@react-pdf-viewer/default-layout";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";
import { ReactComponent as CloseIcon } from "assets/icons/white-close-icon.svg";
import * as pdfjs from "pdfjs-dist";
import { Maybe } from "utils/general/maybe";

import { Loader } from "ui/loader";
import { Paragraph1 } from "ui/typography";

import { IPdfViewerProps } from "./types";

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js`;

const FIRST_PAGE = 1;
const renderToolbar = (Toolbar: (props: ToolbarProps) => ReactElement) => (
  <Toolbar>
    {(slots: ToolbarSlot) => {
      const {
        CurrentPageInput,
        GoToNextPage,
        GoToPreviousPage,
        NumberOfPages,
        ShowSearchPopover,
        Zoom,
        ZoomIn,
        ZoomOut,
      } = slots;
      return (
        <Box className="flex w-full items-center h-10 px-4">
          <ToolbarBox>
            <ShowSearchPopover />
          </ToolbarBox>
          <ToolbarBox>
            <ZoomOut />
          </ToolbarBox>
          <ToolbarBox>
            <Zoom />
          </ToolbarBox>
          <ToolbarBox>
            <ZoomIn />
          </ToolbarBox>
          <ToolbarBox className="ml-auto">
            <GoToPreviousPage />
          </ToolbarBox>
          <ToolbarBox>
            <CurrentPageInput /> / <NumberOfPages />
          </ToolbarBox>
          <ToolbarBox>
            <GoToNextPage />
          </ToolbarBox>
        </Box>
      );
    }}
  </Toolbar>
);

export const PdfViewer = (props: IPdfViewerProps) => {
  const { pdfUrl, isContentWrapperBelow600, isContentWrapperBelow745 } = props;
  const defaultLayoutPluginInstance = defaultLayoutPlugin({
    sidebarTabs: () => [],
    renderToolbar,
  });

  const [isOpen, setIsOpen] = useState(false);
  const [thumbnail, setThumbnail] = useState<string | null>(null);

  const handleTogglePdfViewer = () => {
    setIsOpen(!isOpen);
  };

  const onGenerateThumbnail = async () => {
    const pdf = await pdfjs.getDocument(pdfUrl).promise;
    const page = await pdf.getPage(FIRST_PAGE);
    const viewport = page.getViewport({ scale: 0.5 });

    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d")!;
    canvas.width = viewport.width;
    canvas.height = viewport.height;

    const renderContext = {
      canvasContext: context,
      viewport: viewport,
    };
    await page.render(renderContext).promise;
    setThumbnail(canvas.toDataURL());
  };

  useEffect(() => {
    onGenerateThumbnail();
  }, []);

  return (
    <>
      <Box
        className={`border-[#E4E4E4] border-[1px] rounded-[7.43px] ${isContentWrapperBelow600 ? "w-fit h-[235px]" : "h-[203.36px] w-[230px]"}`}
      >
        <Box
          css={{
            position: "relative",
            display: "flex",
            width: "100%",
            ...(!thumbnail
              ? {
                  height: "132.79px",
                  justifyContent: "center",
                }
              : {}),

            "&::before": {
              content: '""',
              position: "absolute",
              top: "0",
              left: "0",
              width: "100%",
              height: "100%",
              backgroundColor: "#00000045",
              zIndex: 1,
              borderTopLeftRadius: "7.43px",
              borderTopRightRadius: "7.43px",
            },
          }}
        >
          {thumbnail ? (
            <img
              src={thumbnail}
              alt="PDF's Thumbnail"
              className="object-cover h-[132.79px] w-full"
            />
          ) : (
            <Loader loadingText="Loading thumbnail..." />
          )}
        </Box>
        <Box
          className={`flex gap-[11.14px] items-center px-[15px] h-[calc(100%-132.79px)] ${isContentWrapperBelow600 || isContentWrapperBelow745 ? "justify-center" : ""}`}
        >
          <span
            onClick={handleTogglePdfViewer}
            className="text-[#00535C] text-sm leading-[22.29px] font-medium h-[41.57px] w-[109.57px] rounded-[7.43px] py-[9.29px] px-[16.71px] border-[1px] border-[#D0D5DD] text-center cursor-pointer"
          >
            View
          </span>
          <a
            href={pdfUrl}
            download
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#00535C] text-sm leading-[22.29px] font-medium h-[41.57px] w-[109.57px] rounded-[7.43px] py-[9.29px] px-[16.71px] border-[1px] border-[#D0D5DD] text-center"
          >
            Download
          </a>
        </Box>
      </Box>
      <Maybe condition={isOpen}>
        <Box
          css={{
            position: "fixed",
            width: "100vw",
            height: "100vh",
            top: 0,
            left: 0,
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 50000,
          }}
        >
          <Box
            className="flex cursor-pointer items-center gap-2 w-[84px] absolute top-3 right-3"
            onClick={handleTogglePdfViewer}
          >
            <CloseIcon />
            <Paragraph1
              fontSize="18.5px"
              lineHeight="24px"
              textColor="#ffffff"
              fontWeight="700"
            >
              Close
            </Paragraph1>
          </Box>
          <Box
            css={{
              position: "relative",
              width: "70%",
              height: "85%",
              backgroundColor: "#fff",
              borderRadius: "8px",
              overflow: "hidden",
              boxShadow: "0 4px 15px rgba(0, 0, 0, 0.2)",
            }}
          >
            <Worker
              workerUrl={`https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js`}
            >
              <Viewer
                fileUrl={pdfUrl}
                plugins={[defaultLayoutPluginInstance]}
              />
            </Worker>
          </Box>
        </Box>
      </Maybe>
    </>
  );
};

const ToolbarBox = styled.div`
  padding: 0 2px;
  display: flex;
  align-items: center;
  height: 32px;

  > div {
    height: 32px;
  }
`;
